/**
 * Market overview (ARCHITECTURE.md §8) — a read-only situational display: headline
 * indices with intraday sparklines, the SPDR sector heatmap, the CNN Fear & Greed
 * gauge (+ its 90-day trend), and merged market news. Everything here is
 * display-only third-party data (Yahoo/CNN/RSS) carried as plain numbers — no
 * money, no orders, no branded Decimal.
 *
 * Like the dashboard, every panel owns its own loading / error / empty state so a
 * feed outage degrades one card, never the page. The route is session-only (no
 * feature gate): the endpoints don't depend on a Toss credential.
 */
import type { ReactNode } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useI18n } from "@richrich/i18n/react";
import {
  Card,
  FearGreedGauge,
  SectorHeatmap,
  Skeleton,
  Sparkline,
  StockTreemap,
  cn,
  paletteFor,
  treemapTileStyle,
  useResolvedTheme,
  type FearGreedDelta,
} from "@richrich/ui";
import {
  StockSectorSchema,
  type IndexQuote,
  type MarketStocksResponse,
  type StockSector,
} from "@richrich/contracts";
import type { Locale, MessageKey } from "@richrich/i18n";
import { settingsQuery } from "../lib/query";
import {
  FGI_HISTORY_DAYS,
  emptyIndices,
  emptyNews,
  emptySectors,
  emptyStocks,
  fgiHistoryQuery,
  fgiQuery,
  indicesQuery,
  newsQuery,
  sectorsQuery,
  stocksQuery,
} from "../features/market/api";
import { EmptyPanel, ErrorPanel } from "../features/dashboard/panels";

/** Which heatmap the Sectors card is showing. `sectors` is the default (param omitted). */
type HeatView = "sectors" | "stocks";

/** Parse `?heat=` / `?sector=` tolerantly — garbage falls back to the defaults. */
function parseHeatSearch(search: Record<string, unknown>): {
  heat?: HeatView;
  sector?: StockSector;
} {
  const out: { heat?: HeatView; sector?: StockSector } = {};
  if (search.heat === "stocks") out.heat = "stocks";
  const sector = StockSectorSchema.safeParse(search.sector);
  if (sector.success) out.sector = sector.data;
  return out;
}

export const Route = createFileRoute("/market")({
  validateSearch: parseHeatSearch,
  loader: ({ context }) => {
    const { queryClient } = context;
    // Non-blocking cache warm — panels still own their loading states. `/stocks`
    // is feature-gated (Toss), so it's fetched lazily by the card, not prefetched.
    void queryClient.prefetchQuery(indicesQuery);
    void queryClient.prefetchQuery(sectorsQuery);
    void queryClient.prefetchQuery(fgiQuery);
    void queryClient.prefetchQuery(fgiHistoryQuery(FGI_HISTORY_DAYS));
    void queryClient.prefetchQuery(newsQuery);
  },
  component: MarketPage,
});

/** SPDR sector → localized display-name key (drill-down header + summary). */
const SECTOR_NAME_KEY = {
  XLK: "market.sectorXLK",
  XLF: "market.sectorXLF",
  XLE: "market.sectorXLE",
  XLV: "market.sectorXLV",
  XLI: "market.sectorXLI",
  XLY: "market.sectorXLY",
  XLP: "market.sectorXLP",
  XLU: "market.sectorXLU",
  XLB: "market.sectorXLB",
  XLRE: "market.sectorXLRE",
  XLC: "market.sectorXLC",
} as const satisfies Record<StockSector, MessageKey>;

// ── formatting helpers (display-only numbers) ───────────────────────────────

/** `5,432.10` — locale-grouped index/price level (never money; 2 dp). */
function formatLevel(value: number, locale: Locale): string {
  return value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** `+1.24%` / `-0.30%` — the printed sign is the non-color status channel. */
function formatSignedPct(pct: number): string {
  const v = pct.toFixed(2);
  return pct > 0 ? `+${v}%` : `${v}%`;
}

/** `14:32` — clock-only caption from an epoch-ms `asOf`. */
function formatClock(epochMs: number, locale: Locale): string {
  return new Date(epochMs).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

/** Locale-aware relative time (`2h ago` / `3일 전`) for news timestamps. */
function formatRelative(epochMs: number, locale: Locale): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const diffMs = epochMs - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  const absMin = Math.abs(diffMin);
  if (absMin < 60) return rtf.format(diffMin, "minute");
  const diffHr = Math.round(diffMs / 3_600_000);
  if (Math.abs(diffHr) < 24) return rtf.format(diffHr, "hour");
  const diffDay = Math.round(diffMs / 86_400_000);
  return rtf.format(diffDay, "day");
}

/** VIX inverts tone semantics: a rising VIX reads critical, a falling one good. */
function isVix(symbol: string): boolean {
  return /VIX/i.test(symbol);
}

// ── page ─────────────────────────────────────────────────────────────────────

function MarketPage(): ReactNode {
  const { t } = useI18n();

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-8 py-7">
      <header className="border-b border-hairline pb-5">
        <div className="mb-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-muted">
          {t("market.eyebrow")}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">{t("market.title")}</h1>
        <p className="mt-1.5 max-w-2xl text-sm text-ink-secondary">{t("market.description")}</p>
      </header>

      <IndicesRow />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <FgiCard />
        <div className="lg:col-span-2">
          <HeatmapCard />
        </div>
      </div>

      <NewsPanel />
    </div>
  );
}

// ── as-of caption ─────────────────────────────────────────────────────────────

function AsOf({ epochMs }: { epochMs: number }): ReactNode {
  const { t, locale } = useI18n();
  if (!epochMs) return null;
  return (
    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
      {t("market.asOf", { time: formatClock(epochMs, locale) })}
    </span>
  );
}

// ── index tiles ────────────────────────────────────────────────────────────

function IndexTile({ quote }: { quote: IndexQuote }): ReactNode {
  const { locale } = useI18n();
  const resolved = useResolvedTheme();
  const palette = paletteFor(resolved);

  // Tone (color) — inverted for VIX — is separate from the always-printed sign.
  const rawSign = Math.sign(quote.changePercent);
  const toneSign = isVix(quote.symbol) ? -rawSign : rawSign;
  const colorClass =
    toneSign > 0 ? "text-good-text" : toneSign < 0 ? "text-critical" : "text-muted";
  const sparkColor = toneSign > 0 ? palette.good : toneSign < 0 ? palette.critical : palette.muted;

  return (
    <Card>
      {/* Name gets the full tile width (no % beside it — long names truncated). */}
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-ink" title={quote.name}>
          {quote.name}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted">
          {quote.symbol}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-2">
        <div className="text-lg font-semibold tabular-nums text-ink">
          {formatLevel(quote.price, locale)}
        </div>
        <span className={cn("shrink-0 text-xs font-medium tabular-nums", colorClass)}>
          {formatSignedPct(quote.changePercent)}
        </span>
      </div>
      {quote.sparkline.length > 1 ? (
        <Sparkline
          data={quote.sparkline}
          color={sparkColor}
          theme={resolved}
          width={160}
          height={34}
          className="mt-2 w-full"
          ariaLabel={`${quote.name} intraday`}
        />
      ) : (
        <div
          className="mt-2 h-[2px] w-full rounded-full"
          style={{ backgroundColor: palette.grid }}
          aria-hidden="true"
        />
      )}
    </Card>
  );
}

function IndexTileSkeleton(): ReactNode {
  return (
    <Card>
      <Skeleton width={100} height={14} rounded />
      <Skeleton className="mt-3" width={80} height={20} />
      <Skeleton className="mt-3" height={34} />
    </Card>
  );
}

function IndicesRow(): ReactNode {
  const { t } = useI18n();
  const query = useQuery(indicesQuery);

  const header = (
    <div className="flex items-center justify-between gap-3">
      <h2 className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-muted">
        {t("market.indicesTitle")}
      </h2>
      {query.data ? <AsOf epochMs={query.data.asOf} /> : null}
    </div>
  );

  if (query.isError) {
    return (
      <section className="flex flex-col gap-3">
        {header}
        <ErrorPanel
          title={t("market.indicesTitle")}
          error={query.error}
          onRetry={() => query.refetch()}
          height={140}
        />
      </section>
    );
  }

  const data = query.data ?? emptyIndices();
  return (
    <section className="flex flex-col gap-3">
      {header}
      {query.isPending ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }, (_, i) => (
            <IndexTileSkeleton key={i} />
          ))}
        </div>
      ) : data.indices.length === 0 ? (
        <EmptyPanel message={t("market.indicesEmpty")} height={120} />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {data.indices.map((quote) => (
            <IndexTile key={quote.symbol} quote={quote} />
          ))}
        </div>
      )}
    </section>
  );
}

// ── Fear & Greed ─────────────────────────────────────────────────────────────

function FgiCard(): ReactNode {
  const { t } = useI18n();
  const fgi = useQuery(fgiQuery);
  const history = useQuery(fgiHistoryQuery(FGI_HISTORY_DAYS));

  if (fgi.isError) {
    return (
      <ErrorPanel
        title={t("market.fgiTitle")}
        error={fgi.error}
        onRetry={() => fgi.refetch()}
        height={260}
      />
    );
  }

  // The gauge observes the document theme itself when `theme` is omitted, so it
  // stays reactive to a live toggle.
  const deltas: FearGreedDelta[] = fgi.data
    ? [
        { label: t("market.fgiPrevClose"), value: fgi.data.previousClose },
        { label: t("market.fgi1Week"), value: fgi.data.previous1Week },
        { label: t("market.fgi1Month"), value: fgi.data.previous1Month },
        { label: t("market.fgi1Year"), value: fgi.data.previous1Year },
      ]
    : [];

  // h-full + justify-between: this card sits beside the taller treemap card, so
  // the 90-day trend anchors to the bottom instead of leaving a dead gap.
  return (
    <Card title={t("market.fgiTitle")} className="h-full">
      {fgi.isPending ? (
        <div className="flex flex-col items-center gap-3 py-4">
          <Skeleton width={220} height={120} />
          <Skeleton width={80} height={28} />
        </div>
      ) : fgi.data === undefined ? (
        <EmptyPanel message={t("market.fgiEmpty")} height={200} />
      ) : (
        <div className="flex h-full flex-col justify-between gap-4">
          <FearGreedGauge score={fgi.data.score} rating={fgi.data.rating} deltas={deltas} />
          <div className="border-t border-hairline pt-3">
            <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted">
              {t("market.fgiTrend")}
            </div>
            {history.isPending ? (
              <Skeleton height={72} />
            ) : history.isError || !history.data || history.data.points.length <= 1 ? (
              <div className="py-2 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
                {t("market.fgiTrendEmpty")}
              </div>
            ) : (
              <Sparkline
                data={history.data.points.map((p) => p.score)}
                color="neutral"
                area
                width={320}
                height={72}
                className="w-full"
                ariaLabel={t("market.fgiTrend")}
              />
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── heatmap card (sectors ↔ stocks) ──────────────────────────────────────────

/** Fixed treemap canvas height — matches the stocks skeleton/empty frames. */
const TREEMAP_HEIGHT = 520;

/** Segmented toggle between the two heatmap views (styled like `PeriodSwitch`). */
function HeatViewToggle({
  value,
  onChange,
}: {
  value: HeatView;
  onChange: (next: HeatView) => void;
}): ReactNode {
  const { t } = useI18n();
  const options: { key: HeatView; label: string }[] = [
    { key: "sectors", label: t("market.heatViewSectors") },
    { key: "stocks", label: t("market.heatViewStocks") },
  ];
  return (
    <div
      role="tablist"
      aria-label={t("market.sectorsTitle")}
      className="inline-flex items-center gap-0.5 rounded-lg border border-hairline bg-page p-0.5"
    >
      {options.map((o) => {
        const active = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.key)}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              active ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink-secondary",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function HeatmapCard(): ReactNode {
  const { t, locale } = useI18n();
  const resolved = useResolvedTheme();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const view: HeatView = search.heat ?? "sectors";
  const selectedSector = search.sector ?? null;

  // Search is fully replaced on each change: an omitted key clears the param, so
  // returning `{}` restores the `sectors` default (and drops any drill-down).
  const setView = (next: HeatView): void =>
    void navigate({ search: next === "sectors" ? {} : { heat: "stocks" } });
  const selectSector = (sector: StockSector): void =>
    void navigate({ search: { heat: "stocks", sector } });
  const clearSector = (): void => void navigate({ search: { heat: "stocks" } });

  const settings = useQuery(settingsQuery);
  const tossUnlocked = settings.data?.features.toss === true;

  const sectors = useQuery(sectorsQuery);
  const stocks = useQuery({ ...stocksQuery, enabled: view === "stocks" && tossUnlocked });

  const asOfSource = view === "sectors" ? sectors.data : stocks.data;

  return (
    <Card
      title={view === "sectors" ? t("market.sectorsTitle") : t("market.stocksTitle")}
      subtitle={view === "sectors" ? t("market.sectorsSubtitle") : t("market.stocksSubtitle")}
      actions={
        <div className="flex items-center gap-3">
          {asOfSource ? <AsOf epochMs={asOfSource.asOf} /> : null}
          <HeatViewToggle value={view} onChange={setView} />
        </div>
      }
    >
      {view === "sectors" ? (
        <SectorsView onSelectSector={selectSector} />
      ) : (
        <StocksView
          locale={locale}
          theme={resolved}
          settingsPending={settings.isPending}
          unlocked={tossUnlocked}
          query={stocks}
          selectedSector={selectedSector}
          onSelectSector={selectSector}
          onClearSector={clearSector}
        />
      )}
    </Card>
  );
}

function SectorsView({
  onSelectSector,
}: {
  onSelectSector: (sector: StockSector) => void;
}): ReactNode {
  const { t } = useI18n();
  const query = useQuery(sectorsQuery);

  if (query.isError) {
    return <ErrorPanel error={query.error} onRetry={() => query.refetch()} bare height={260} />;
  }

  const data = query.data ?? emptySectors();
  if (query.isPending) {
    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} height={78} />
        ))}
      </div>
    );
  }
  // Clicking a sector tile drills into that sector's per-stock treemap.
  return (
    <SectorHeatmap
      sectors={data.sectors}
      emptyLabel={t("market.sectorsEmpty")}
      onSelect={(symbol) => {
        const parsed = StockSectorSchema.safeParse(symbol);
        if (parsed.success) onSelectSector(parsed.data);
      }}
    />
  );
}

function StocksView({
  locale,
  theme,
  settingsPending,
  unlocked,
  query,
  selectedSector,
  onSelectSector,
  onClearSector,
}: {
  locale: Locale;
  theme: ReturnType<typeof useResolvedTheme>;
  settingsPending: boolean;
  unlocked: boolean;
  query: UseQueryResult<MarketStocksResponse>;
  selectedSector: StockSector | null;
  onSelectSector: (sector: StockSector) => void;
  onClearSector: () => void;
}): ReactNode {
  const { t } = useI18n();

  // Undetermined gate (settings still loading) → skeleton, not a CTA flash.
  if (settingsPending) {
    return <Skeleton height={TREEMAP_HEIGHT} />;
  }
  // Gate: no Toss credential → CTA instead of a guaranteed 403 fetch.
  if (!unlocked) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-hairline text-center"
        style={{ minHeight: TREEMAP_HEIGHT }}
      >
        <div className="text-sm font-medium text-ink">{t("nav.setupRequired")}</div>
        <p className="max-w-xs text-xs text-muted">{t("market.stocksGateBody")}</p>
        <Link
          to="/settings"
          className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
        >
          {t("portfolio.openSettings")}
        </Link>
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorPanel
        error={query.error}
        onRetry={() => query.refetch()}
        bare
        height={TREEMAP_HEIGHT}
      />
    );
  }
  if (query.isPending) {
    return <Skeleton height={TREEMAP_HEIGHT} />;
  }

  const data = query.data ?? emptyStocks();
  const count = selectedSector
    ? data.stocks.filter((s) => s.sector === selectedSector).length
    : data.stocks.length;
  const sectorName = selectedSector ? t(SECTOR_NAME_KEY[selectedSector]) : "";
  const ariaLabel = selectedSector
    ? t("market.stocksSummarySector", { count: String(count), sector: sectorName })
    : t("market.stocksSummaryAll", { count: String(count) });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {selectedSector ? (
            <>
              <button
                type="button"
                onClick={onClearSector}
                className="rounded-md border border-hairline bg-page px-2 py-1 text-xs font-medium text-ink-secondary transition-colors hover:bg-elevated hover:text-ink"
              >
                {t("market.stocksBack")}
              </button>
              <span className="text-sm font-medium text-ink">{sectorName}</span>
            </>
          ) : (
            <span className="text-sm font-medium text-ink">{t("market.stocksAll")}</span>
          )}
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
          {data.baselineDate
            ? t("market.stocksBaseline", { date: data.baselineDate })
            : t("market.stocksNoBaseline")}
        </span>
      </div>
      {data.stocks.length === 0 ? (
        <EmptyPanel message={t("market.stocksEmpty")} height={TREEMAP_HEIGHT} />
      ) : (
        <>
          <StockTreemap
            stocks={data.stocks}
            selectedSector={selectedSector}
            onSelectSector={onSelectSector}
            theme={theme}
            height={TREEMAP_HEIGHT}
            ariaLabel={ariaLabel}
            formatPrice={(p) => formatLevel(p, locale)}
            emptyLabel={t("market.stocksEmpty")}
          />
          <TreemapLegend theme={theme} />
        </>
      )}
    </div>
  );
}

/**
 * The tint scale under the treemap: swatches rendered with the EXACT tile ramp
 * ({@link treemapTileStyle}), so the legend can never drift from the tiles. The
 * printed −3%/0/+3% bounds double as the reminder that the tint saturates there.
 */
function TreemapLegend({ theme }: { theme: ReturnType<typeof useResolvedTheme> }): ReactNode {
  const { t } = useI18n();
  const palette = paletteFor(theme);
  const steps = [-3, -2, -1, 0, 1, 2, 3];
  return (
    <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1">
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-[10px] tabular-nums text-muted">-3%</span>
        <div className="flex overflow-hidden rounded-sm border border-hairline">
          {steps.map((pct) => (
            <div key={pct} className="h-3 w-5" style={treemapTileStyle(pct, palette)} />
          ))}
        </div>
        <span className="font-mono text-[10px] tabular-nums text-muted">+3%</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div
          className="h-3 w-5 rounded-sm border border-hairline"
          style={treemapTileStyle(null, palette)}
        />
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
          {t("market.legendNoData")}
        </span>
      </div>
    </div>
  );
}

// ── news ─────────────────────────────────────────────────────────────────────

function NewsPanel(): ReactNode {
  const { t, locale } = useI18n();
  const query = useQuery(newsQuery);

  if (query.isError) {
    return (
      <ErrorPanel
        title={t("market.newsTitle")}
        error={query.error}
        onRetry={() => query.refetch()}
        height={200}
      />
    );
  }

  const data = query.data ?? emptyNews();
  return (
    <Card
      title={t("market.newsTitle")}
      actions={query.data ? <AsOf epochMs={query.data.asOf} /> : null}
    >
      {query.isPending ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} height={20} />
          ))}
        </div>
      ) : data.items.length === 0 ? (
        <EmptyPanel message={t("market.newsEmpty")} height={120} />
      ) : (
        <ul className="flex flex-col divide-y divide-hairline">
          {data.items.map((item, i) => (
            <li key={`${item.link}-${i}`} className="py-2.5 first:pt-0 last:pb-0">
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start justify-between gap-3"
              >
                <span className="min-w-0 flex-1 text-sm text-ink group-hover:text-brand">
                  {item.title}
                </span>
                <span className="flex shrink-0 items-center gap-2 pt-0.5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
                    {item.source}
                  </span>
                  {item.publishedAt !== null ? (
                    <span className="text-[11px] text-muted tabular-nums">
                      {formatRelative(item.publishedAt, locale)}
                    </span>
                  ) : null}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
